import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight, ArrowDownRight, ArrowDownLeft } from "lucide-react";
import { NavbarLogo } from '../media/navbarLogo';

export default function Header() {
  const { texts, currentLanguage, switchLanguage, isRTL } = useLanguage();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [headerContent, setHeaderContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchHeaderContent() {
      try {
        const response = await fetch('/api/header');
        if (!response.ok) {
          throw new Error('Failed to fetch header content');
        }
        const data = await response.json();
        console.log(`data: ${JSON.stringify(data)}`);
        setHeaderContent(data.header);
      } catch (error) {
        console.error('Error fetching header:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchHeaderContent();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLanguageChange = (lang) => {
    switchLanguage(lang);
    setIsDropdownOpen(false);
  };

  // Scroll to Vision component
  const scrollToVision = (e) => {
    e.preventDefault();
    const visionElement = document.getElementById('vision-section');
    if (visionElement) {
      visionElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Scroll to Projects component
  const scrollToProjects = (e) => {
    e.preventDefault();
    const projectsElement = document.getElementById('projects-section');
    if (projectsElement) {
      projectsElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Scroll to Footer component (Contact)
  const scrollToContact = (e) => {
    e.preventDefault();
    const contactElement = document.getElementById('contact-section');
    if (contactElement) {
      contactElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToCompanies = (e) => {
    e.preventDefault();
    const companiesElement = document.getElementById('companies-section');
    if (companiesElement) {
      companiesElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-white py-0 xl:py-4 px-0 xl:px-[5%]">
      <header 
        className="relative bg-cover bg-center h-[75vh] sm:h-[80vh] xl:h-[90vh] xl:rounded-lg overflow-hidden" 
        style={{
          backgroundImage: "url('https://i.ibb.co/ds2Mr1qw/image-16-1.png')",
          margin: "0 auto"
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className={`relative z-10 w-full px-[4%] sm:px-[5%] py-[2vh] flex items-center ${isRTL ? 'flex-row-reverse justify-between' : 'justify-between'}`}>
          <div className={`flex items-center w-[30%] sm:w-[25%] ${isRTL ? 'flex-row-reverse' : ''}`}>
            <NavbarLogo />
          </div>
          <nav className={`hidden xl:block absolute left-1/2 -translate-x-1/2 w-full max-w-[600px] ${isRTL ? 'text-right' : ''}`}>
            <ul className={`flex ${isRTL ? 'justify-center gap-12 flex-row-reverse' : 'justify-center space-x-8'}`}>
              <li>
                <a 
                  href="#vision-section" 
                  onClick={scrollToVision}
                  className="text-white hover:text-gray-200 transition-colors whitespace-nowrap"
                >
                  {texts.header.navigation.about}
                </a>
              </li>
              <li>
                <a 
                  href="#projects-section" 
                  onClick={scrollToCompanies}
                  className="text-white hover:text-gray-200 transition-colors whitespace-nowrap"
                >
                  {texts.header.navigation.projects}
                </a>
              </li>
              <li>
                <a 
                  href="#contact-section" 
                  onClick={scrollToContact}
                  className="text-white hover:text-gray-200 transition-colors whitespace-nowrap"
                >
                  {texts.header.navigation.contact}
                </a>
              </li>
            </ul>
          </nav>
          <div className={`text-white relative w-[30%] sm:w-[25%] flex ${isRTL ? 'justify-start' : 'justify-end'}`} ref={dropdownRef}>
            <button 
              className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
              onClick={toggleDropdown}
            >
              <span>{currentLanguage}</span>
              <svg 
                className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''} ${isRTL ? 'mr-2' : 'ml-2'}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
            
            {isDropdownOpen && (
              <div className={`absolute mt-2 w-24 bg-white rounded-md shadow-lg py-1 z-20 ${isRTL ? 'left-0' : 'right-0'}`}>
                <button 
                  className={`block w-full px-4 py-2 text-gray-800 hover:bg-gray-100 ${isRTL ? 'text-right' : 'text-left'}`}
                  onClick={() => handleLanguageChange('EN')}
                >
                  EN
                </button>
                <button 
                  className={`block w-full px-4 py-2 text-gray-800 hover:bg-gray-100 ${isRTL ? 'text-right' : 'text-left'}`}
                  onClick={() => handleLanguageChange('BA')}
                >
                  BA
                </button>
                <button 
                  className={`block w-full px-4 py-2 text-gray-800 hover:bg-gray-100 ${isRTL ? 'text-right' : 'text-left'}`}
                  onClick={() => handleLanguageChange('AR')}
                >
                  AR
                </button>
              </div>
            )}
          </div>
        </div>        
        <div className="absolute bottom-[5vh] sm:bottom-[8vh] xl:bottom-[10vh] left-0 right-0 z-10 w-full px-[4%] sm:px-[5%]">
        <div className={`w-full max-w-[90rem] mx-auto flex flex-col xl:flex-row ${isRTL ? 'xl:flex-row-reverse' : ''} gap-[1rem] xl:gap-[2rem] xl:justify-between xl:items-end`}>
          <div className={`text-white max-w-[100%] xl:max-w-[60%] ${isRTL ? 'text-right' : 'text-left'}`}>

          <h1 className={`text-[2rem] sm:text-[3rem] xl:text-[4rem] font-bold leading-tight ${
                !isRTL ? 'whitespace-pre-line' : ''
              }`}>
                {!isRTL 
                  ? headerContent?.title[currentLanguage].replace(',', ',\n')
                  : headerContent?.title[currentLanguage]}
              </h1>

              <p className="mt-[0.5rem] sm:mt-[1rem] text-[1.25rem] sm:text-[1.75rem] xl:text-[2.25rem]">
                {headerContent?.subtitle[currentLanguage]}
              </p>
              <div className="hidden">
                <div className="bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-md">
                  <ArrowDownLeft
                    size={24} 
                    className="text-black" 
                  />
                </div>
              </div>
            </div>
            
            <button 
              onClick={scrollToVision}
              className={`hidden xl:block group relative w-full xl:w-[20%] min-w-[200px] ${isRTL ? 'text-left' : ''} h-[3rem] sm:h-[3.5rem] rounded-full bg-white text-matt-black shadow-[inset_-2px_2px_4px_#00000040] hover:bg-white overflow-hidden`}>
              <div className="absolute inset-0 w-0 bg-gradient-to-r from-[#295CAD] to-[#78C9FF] transition-all duration-1000 ease-in-out group-hover:w-full"></div>
              
              <div className="absolute left-0 top-1/2 -translate-y-1/2 transition-all duration-1000 ease-in-out group-hover:left-[calc(100%-3rem)] group-hover:rotate-90 z-10">
                <div className="bg-white rounded-full w-12 h-12 flex items-center justify-center border border-gray-200">
                  <ArrowDownRight 
                    size={20} 
                    className="text-black" 
                  />
                </div>
              </div>
              <span className={`relative text-black z-10 text-[0.875rem] font-medium transition-colors px-6
                duration-1000 group-hover:text-white ${isRTL ? 'text-center mr-[10px] block w-full' : ''}`}>
                {texts.header.hero.button}
              </span>
            </button>
          </div>
        </div>
      </header>
    </div>
  );
}

  